import { useCallback, useMemo, useState } from "react";
import { Button, Flex, SegmentedControl, Text, TextInput } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { FormulasGrid, type Formula } from "./FormulasGrid";
import * as styles from "./styles_vanilla.css";
import type { ViewFilter } from "../types";

type FormulasPanelProps = {
  formulas: Formula[];
  viewFilter: ViewFilter;
  onViewFilterChange: (filter: ViewFilter) => void;
  onEditFormula?: (formulaId: string) => void;
  onNewFormula?: () => void;
  isDataPackageArchived?: boolean;
};

export const FormulasPanel = ({
  formulas,
  viewFilter,
  onViewFilterChange,
  onEditFormula,
  onNewFormula,
  isDataPackageArchived = false,
}: FormulasPanelProps) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredFormulas = useMemo(() => {
    if (isDataPackageArchived) return formulas;
    return formulas.filter((f) =>
      viewFilter === "active" ? f.isActive !== false : f.isActive === false,
    );
  }, [formulas, viewFilter, isDataPackageArchived]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleNewFormula = useCallback(() => {
    onNewFormula?.();
  }, [onNewFormula]);

  return (
    <Flex flexDirection="column" className={styles.formulasPanel}>
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap={SPACING.MEDIUM}
        className={styles.formulasPanelHeader}
      >
        <Text variant="h3">Formulas</Text>
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <TextInput
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search formulas"
            icon="search"
            isClearable
            onClearValue={handleClearSearch}
            data-testid="formulas-search"
          />
          {!isDataPackageArchived && (
            <SegmentedControl
              value={viewFilter}
              onChange={(val) => onViewFilterChange(val as ViewFilter)}
              options={[
                { label: "Active", value: "active" },
                { label: "Archived", value: "archived" },
              ]}
              data-testid="formulas-view-filter"
            />
          )}
          {onNewFormula && (
            <Button
              variant="primary"
              icon="plus"
              onClick={handleNewFormula}
              data-testid="new-formula-btn"
            >
              New formula
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex flexGrow={1} className={styles.formulasTableContainer}>
        <FormulasGrid
          formulas={filteredFormulas}
          searchValue={searchValue}
          viewFilter={isDataPackageArchived ? "archived" : viewFilter}
          onEditFormula={onEditFormula}
        />
      </Flex>
    </Flex>
  );
};
